import React, { useCallback } from 'react';
import { Box, styled, Tooltip, Typography, typographyClasses } from '@mui/material';
import { DateTime } from 'luxon';
import { DateObject } from 'react-multi-date-picker';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { useFormContext } from 'react-hook-form';
import { WorkFormContext } from 'shared/components/work-forms/work-form.types';
import { LwButton, MultiDateCalendar } from 'redesign';
import { IconCaretLeft, IconTrash } from 'assets/img';
import { useTranslation } from 'react-i18next';
import { dateObjectToDateTime, dateTimeToDateObject } from './multi-date-object-utils';

export type MultiplyDatesFormContext = {
  additionalCreateDates: DateTime[];
};

type Props = { disabledDate?: DateTime | null; multiplySectionAllowsPast: boolean };

export const MultiplyDatesCalendarInput = ({ disabledDate, multiplySectionAllowsPast }: Props) => {
  const [dates, setDates] = React.useState<DateTime[]>([]);
  const { setValue } = useFormContext<WorkFormContext>();

  const { monthLabels, dayLabels } = useCalendarDefaults();
  const today = DateTime.now().startOf('day');
  const minAvailableDate = multiplySectionAllowsPast ? null : today;
  const disabledDateObject = disabledDate ? dateTimeToDateObject(disabledDate) : null;
  const { t } = useTranslation('web-common');

  const onChange = useCallback(
    (date: DateObject | DateObject[]) => {
      if (!date) {
        return;
      }

      const isEnabled = (when: DateObject) => when.unix !== disabledDateObject?.unix;
      const selectedDates = (Array.isArray(date) ? date : [date])
        .filter(isEnabled)
        .map(dateObjectToDateTime)
        .sort((a, b) => a.toMillis() - b.toMillis());

      setValue('additionalCreateDates', selectedDates);
      setDates(selectedDates);
    },
    [disabledDateObject, setValue]
  );

  return (
    <StyledCalendarGrid>
      <CalendarWrapper data-testid="calendar">
        <MultiDateCalendar
          value={dates.map(dateTimeToDateObject)}
          currentDate={dateTimeToDateObject(today)}
          mapDays={({ date, isSameDate }) => ({
            children: date.format('DD'),
            className:
              disabledDateObject && isSameDate(date, disabledDateObject) ? 'shift-date' : undefined,
          })}
          onChange={onChange}
          weekStartDayIndex={1}
          months={monthLabels}
          weekDays={dayLabels}
          {...(minAvailableDate ? { minDate: dateTimeToDateObject(minAvailableDate) } : {})}
          buttons
          multiple
          disableMonthPicker
          disableYearPicker
          numberOfMonths={2}
          renderButton={CalendarNavigationButton}
        />
        <CalendarInfoContainer>
          <>
            {dates.length && dates.length <= 5 ? (
              <Box>
                <Typography variant="body1">Geselecteerde data:</Typography>
                <Typography variant="body2">
                  {dates.map((date) => date.toFormat('dd-MM-yyyy')).join(', ')}
                </Typography>
              </Box>
            ) : dates.length ? (
              <TooltipBox>
                <Tooltip title={TooltipDates(dates)}>
                  <TooltipHoverText>Geselecteerde data</TooltipHoverText>
                </Tooltip>
              </TooltipBox>
            ) : (
              <Box />
            )}
          </>
          {/* TODO: tertiary button https://levelworks.atlassian.net/browse/LW1-22847 */}
          <LwButton
            iconColorMode="stroke"
            startIcon={<IconTrash width={18} height={18} />}
            color="tertiary"
            sx={{
              '&.MuiButton-root': {
                border: 'none',
                '&:hover': {
                  border: 'none',
                  boxShadow: 'none',
                  textDecoration: 'underline',
                },
              },
            }}
            onClick={() => {
              setValue('additionalCreateDates', []);
              setDates([]);
            }}
          >
            {t('buttons.delete', { ns: 'web-common' })}
          </LwButton>
        </CalendarInfoContainer>
      </CalendarWrapper>
    </StyledCalendarGrid>
  );
};

const TooltipDates = (dates: DateTime[]) => {
  return (
    <TooltipBody>
      {dates.map((date) => (
        <TooltipTypography key={date.toISO()}>{date.toFormat('dd-MM-yyyy')}</TooltipTypography>
      ))}
    </TooltipBody>
  );
};

const CalendarNavigationButton = (
  direction: string,
  handleClick: React.MouseEventHandler<HTMLButtonElement> | undefined,
  disabled: boolean
) => {
  if (disabled) {
    return <StyledArrowButton disabled />;
  }

  return (
    <StyledArrowButton onClick={handleClick}>
      <StyledArrow style={direction === 'right' ? { transform: 'rotate(180deg)' } : {}} />
    </StyledArrowButton>
  );
};

const StyledCalendarGrid = styled('section')(({ theme }) => ({
  padding: theme.spacing(0, 3),
}));

const dateSize = '36px';

const CalendarWrapper = styled(Box)(({ theme }) => ({
  '&& .rmdp-week-day': {
    width: dateSize,
    height: dateSize,
  },

  '&& .rmdp-day': {
    width: dateSize,
    height: dateSize,
    margin: '2px',
    '& span.shift-date': {
      background: theme.palette.lwPrimaryAccentLight[100],
      color: 'white',
      borderRadius: theme.spacing(5),
      pointerEvents: 'none',
      cursor: 'default',
    },
  },

  '&& .rmdp-header': {
    height: 'auto',
    '& .rmdp-header-values': {
      '& > span': {
        paddingLeft: '1ch',
      },
    },
  },
}));

const CalendarInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  margin: theme.spacing(2, 0),
}));

const TooltipHoverText = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    color: theme.palette.lwPrimaryAccentDark[100],
    fontSize: '14px',
    fontFamily: 'Urbanist',
    fontVariationSettings: '"wght" 500',
    fontWeight: 600,
    minHeight: '32px',
    lineHeight: theme.spacing(5),
    padding: theme.spacing(2, 3),
    margin: 0,
    letterSpacing: 0,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const TooltipBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const TooltipBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-evenly',
  gap: theme.spacing(1),
}));

const TooltipTypography = styled(Typography)(() => ({
  [`&.${typographyClasses.root}`]: { color: 'white', fontSize: '12px' },
}));

const StyledArrow = styled(IconCaretLeft)(() => ({
  height: '27px',
  width: '27px',
}));

const StyledArrowButton = styled('button')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  height: '39px',
  width: '39px',
  background: 'inherit',
  border: 'none',
  borderRadius: 8,
  padding: 0,

  '&:not(:disabled):hover': {
    background: theme.palette.lwPrimaryAccentLight[10],
    cursor: 'pointer',
    '& svg': {
      '& path': { stroke: theme.palette.lwPrimaryAccentDark[100] },
    },
  },
}));
